import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface PromptDialogConfig {
    title: string;
    description: string;
    inputLabel?: string;
    inputPlaceholder?: string;
    inputRequired?: boolean;
    confirmButtonText?: string;
    cancelButtonText?: string;
    confirmButtonColor?: 'primary' | 'accent' | 'warn';
}

export interface PromptDialogResult {
    confirmed: boolean;
    value: string;
}

@Component({
    selector: 'shared-prompt',
    imports: [
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        CommonModule,
        FormsModule,
    ],
    template: `
        <h2 mat-dialog-title class="prompt-title">
            {{ config.title }}
            <button class="close-button" mat-icon-button (click)="cancel()">
                <mat-icon>close</mat-icon>
            </button>
        </h2>
        <mat-dialog-content class="prompt-content">
            <div class="description" [innerHTML]="formattedDescription"></div>
            <mat-form-field appearance="outline" class="input-field">
                <mat-label>{{ config.inputLabel || 'Enter value' }}</mat-label>
                <input
                    matInput
                    [(ngModel)]="inputValue"
                    [placeholder]="config.inputPlaceholder || ''"
                    (keyup.enter)="confirm()"
                    #inputRef
                />
            </mat-form-field>
        </mat-dialog-content>
        <mat-dialog-actions align="end" class="prompt-actions">
            <button mat-stroked-button (click)="cancel()" class="cancel-btn">
                {{ config.cancelButtonText || 'Cancel' }}
            </button>
            <button
                mat-flat-button
                [color]="config.confirmButtonColor || 'primary'"
                (click)="confirm()"
                [disabled]="config.inputRequired && !inputValue.trim()"
                class="confirm-btn"
            >
                {{ config.confirmButtonText || 'Confirm' }}
            </button>
        </mat-dialog-actions>
    `,
    styles: [
        `
            :host {
                display: block;
            }

            .prompt-title {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin: 0;
                padding: 16px 24px;
                font-size: 1.25rem;
                font-weight: 500;
                border-bottom: 1px solid rgba(0, 0, 0, 0.12);
            }

            :host-context(.dark-theme) .prompt-title {
                border-bottom-color: rgba(255, 255, 255, 0.1);
            }

            .close-button {
                margin-right: -8px;
            }

            .prompt-content {
                min-width: 400px;
                max-width: 500px;
                padding: 24px !important;
            }

            .description {
                font-size: 0.95rem;
                line-height: 1.6;
                color: rgba(0, 0, 0, 0.87);
                white-space: pre-wrap;
                margin-bottom: 16px;
            }

            :host-context(.dark-theme) .description {
                color: rgba(255, 255, 255, 0.85);
            }

            .description ::ng-deep p {
                margin: 0 0 12px 0;
            }

            .description ::ng-deep p:last-child {
                margin-bottom: 0;
            }

            .input-field {
                width: 100%;
            }

            .prompt-actions {
                padding: 16px 24px !important;
                border-top: 1px solid rgba(0, 0, 0, 0.12);
                gap: 12px;
            }

            :host-context(.dark-theme) .prompt-actions {
                border-top-color: rgba(255, 255, 255, 0.1);
            }

            .cancel-btn {
                min-width: 100px;
            }

            .confirm-btn {
                min-width: 100px;
            }
        `,
    ],
})
// eslint-disable-next-line @angular-eslint/component-class-suffix -- public API: exported from the barrel and imported by class name (MatDialog.open(PromptDialog, ...)) from e2e; renaming would break every consumer
export class PromptDialog {
    inputValue = '';

    constructor(
        @Inject(MAT_DIALOG_DATA) public config: PromptDialogConfig,
        private dialogRef: MatDialogRef<PromptDialog, PromptDialogResult>
    ) {}

    /**
     * Format the description to handle newlines and bullet points
     */
    get formattedDescription(): string {
        if (!this.config.description) return '';

        // Convert newlines to <br> and wrap in paragraphs
        return this.config.description
            .split('\n\n')
            .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
            .join('');
    }

    confirm(): void {
        if (this.config.inputRequired && !this.inputValue.trim()) {
            return;
        }
        this.dialogRef.close({
            confirmed: true,
            value: this.inputValue?.trim() || '',
        });
    }

    cancel(): void {
        this.dialogRef.close({
            confirmed: false,
            value: '',
        });
    }
}
